import Permission from '@constants/permission'
import ProductForm from '@pages/products/form'
import ProductsStores from '@components/pages/products/products-stores'
import ProfileWidget from '@shared/profile-widget'
import React, { ReactElement, useEffect, useState } from 'react'
import hasPerms from '@utils/has-permission'
import useAllActions from '@hooks/use-all-actions'
import useSelector from '@hooks/use-selector'
import { AppDispatch, ProductOperatingState, ProductType } from '@types'
import { CircularProgress } from '@mui/material'
import {
  Edit as EditIcon,
  RestorePage as RestoreIcon,
  Delete as TrashIcon,
} from '@mui/icons-material'
import { getErrorMessage } from '@utils/string-utils'
import { getTags } from '@store/actions/tags'
import { makeStyles } from 'tss-react/mui'
import { removeProductsStores } from '@store/actions/products-stores'
import { useConfirm } from '@shared/confirm-dialog'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import ProductAttributes from './products/product-attributes/product-attributes'
import {
  addProductToStores,
  fetchProduct,
  fetchProductsStoresForProductId,
  removeProductAtStore,
  removeProductFromStores,
  setPriceForVariantAtStore,
  updateProduct,
} from '@services/product-service'
import {
  getProductsStoresForProduct,
  selectProduct,
} from '@store/selectors/products-stores'

const Product = (): ReactElement => {
  const { classes } = useStyles()
  const { productId } = useParams<{ productId: string }>()
  const id = parseInt(productId as string, 10)

  const dispatch = useDispatch<AppDispatch>()
  const confirm = useConfirm()
  const { setFlashMessage } = useAllActions()

  const product: ProductType = useSelector(selectProduct, id)
  const productsStores = useSelector(getProductsStoresForProduct, id)
  const editable = hasPerms(Permission.PRODUCTS_UPDATE)

  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)

  const fetchProductsStores = () =>
    dispatch(fetchProductsStoresForProductId(id))

  const fetchData = async () => {
    try {
      setLoading(true)
      await Promise.all([
        dispatch(fetchProduct(id)),
        fetchProductsStores(),
        dispatch(getTags()),
      ])
    } catch (error) {
      setFlashMessage(getErrorMessage(error), 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  const handleProductUpdate = async (productData) => {
    await dispatch(updateProduct(id, productData))
  }

  const handleAddProductToStores = (storeIds: number[]) =>
    dispatch(addProductToStores(id, storeIds))

  const handleRemoveProductFromStores = (storeIds: number[]) =>
    dispatch(removeProductFromStores(id, storeIds))

  const handleRemoveProductAtStore = (storeId: number) =>
    dispatch(removeProductAtStore(storeId, id))

  const handleSetStoreVariantPrice = (
    storeId: number,
    variantId: number,
    price: number,
  ) => dispatch(setPriceForVariantAtStore(storeId, variantId, price))

  const handleToggleEditing = () => setIsEditing(!isEditing)

  const handleConfirmArchive = async () => {
    await handleProductUpdate({ archived_at: true })
    dispatch(removeProductsStores(productsStores))
    await fetchData()
  }

  const handleConfirmRestore = async () => {
    await handleProductUpdate({ archived_at: null })
    await fetchData()
  }

  const handleTriggerArchive = () => {
    confirm({
      cancelText: 'Nevermind',
      confirmText: 'Archive',
      message:
        'Are you sure you want to completely remove this product from the ' +
        'Philz database? The product will immediately disappear from all stores.' +
        'Location assignments will be removed and cannot be restored.',
      onConfirm: handleConfirmArchive,
      title: 'Confirm archival of Product!',
    })
  }

  const handleTriggerRestore = () => {
    confirm({
      cancelText: 'Nevermind',
      confirmText: 'Restore',
      message:
        'Restoring this product will recreate the product and most of its ' +
        'values. However it will not restore a product to stores immediately.',
      onConfirm: handleConfirmRestore,
      title: 'Return product to database',
    })
  }

  if (loading || !product) return <CircularProgress />

  const isArchived = !!product.archived_at

  const title =
    product.name +
    (product.operating_state === ProductOperatingState.hidden
      ? ' - Hidden'
      : '')

  return (
    <div>
      <ProfileWidget
        buttons={[
          !isArchived && {
            icon: <EditIcon />,
            onClick: handleToggleEditing,
            title: 'Edit Product',
          },
          editable &&
            !isArchived && {
              icon: <TrashIcon />,
              onClick: handleTriggerArchive,
              title: 'Archive Product',
            },
          editable &&
            isArchived && {
              icon: <RestoreIcon />,
              onClick: handleTriggerRestore,
              title: 'Restore Product',
            },
        ]}
        imageUrl={product.image_url}
        subtitle1={product.description}
        subtitle2={product.tags?.map((tag) => tag.name).join(', ')}
        title={title}
      />
      {isEditing && (
        <div className={classes.section}>
          <ProductForm
            canEdit={editable}
            className={classes.editForm}
            initialProductData={product}
            onSubmit={handleProductUpdate}
            title='Edit Product'
          />
        </div>
      )}
      {!isArchived && (
        <>
          <ProductAttributes editable={editable} product={product} />
          <ProductsStores
            addProductToStores={handleAddProductToStores}
            className={classes.section}
            fetch={fetchProductsStores}
            product={product}
            productsStores={productsStores}
            removeProductFromStores={handleRemoveProductFromStores}
            removeProductStore={handleRemoveProductAtStore}
            setStoreVariantPrice={handleSetStoreVariantPrice}
          />
        </>
      )}
    </div>
  )
}

export default Product

const useStyles = makeStyles()((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  editForm: {
    marginBottom: theme.spacing(2),
  },
}))
